import React, { useState, useEffect } from 'react';
import { FaSearch, FaUser, FaAngleLeft, FaAngleRight, FaPlus, FaTimes, FaEye, FaTrash, FaCheckCircle, FaEdit, FaUserPlus, FaChartLine, FaUsers } from 'react-icons/fa';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

// Hook personnalisé pour gérer la récupération des clients pour le tableau
const useFetchClients = (searchTerm, page, refreshKey) => {
    const [clients, setClients] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const clientsPerPage = 10;

    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const query = new URLSearchParams({
                    q: searchTerm,
                    page,
                    limit: clientsPerPage,
                }).toString();

                const res = await fetch(`${API_URL}/users?${query}`, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération des clients.");
                }
                const data = await res.json();

                const processedClients = Array.isArray(data?.users) ? data.users.map(client => ({
                    ...client,
                    userType: client.userTypeId === 2 ? 'Client Standard' : 'Inconnu',
                })) : [];

                setClients(processedClients);
                setTotalCount(Number(data?.totalCount) || 0);
            } catch (err) {
                console.error("Erreur API :", err);
                setError("Échec de la récupération des clients. Veuillez réessayer plus tard.");
                setClients([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [searchTerm, page, refreshKey]);

    return { clients, totalCount, loading, error, clientsPerPage };
};

// NOUVEAU HOOK : Pour récupérer et analyser les données pour les visualisations
const useClientSummaryData = (refreshKey) => {
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const API_URL = process.env.REACT_APP_API_URL;
        const accessToken = localStorage.getItem("accessToken");
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
        };

        const fetchAllClients = async () => {
            setLoading(true);
            try {
                // Appel initial pour obtenir le nombre total de clients
                const res = await fetch(`${API_URL}/users?limit=1&page=1`, { headers });
                if (!res.ok) {
                    throw new Error("Échec de la récupération des données client.");
                }
                const initialData = await res.json();
                const totalClients = Number(initialData?.totalCount) || 0;

                // Si pas de clients, retourner des données vides
                if (totalClients === 0) {
                    setSummaryData({
                        totalClients: 0,
                        newClientsThisMonth: 0,
                        chartData: []
                    });
                    setLoading(false);
                    return;
                }

                // Récupérer TOUS les clients en une seule requête avec une limite élevée
                const allRes = await fetch(`${API_URL}/users?limit=${totalClients}&page=1`, { headers });
                if (!allRes.ok) {
                    throw new Error("Échec de la récupération de toutes les données client.");
                }
                const allData = await allRes.json();
                const allClients = Array.isArray(allData?.users) ? allData.users : [];

                // Calcul des nouveaux clients ce mois-ci
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentYear = now.getFullYear();

                const newClientsThisMonth = allClients.filter(client => {
                    if (!client.createdAt) return false;
                    const clientDate = new Date(client.createdAt);
                    return clientDate.getMonth() === currentMonth &&
                        clientDate.getFullYear() === currentYear;
                }).length;

                // Préparation des données pour le graphique des inscriptions mensuelles
                const monthlySignups = allClients.reduce((acc, client) => {
                    if (!client.createdAt) return acc;
                    const clientDate = new Date(client.createdAt);
                    const monthYear = `${clientDate.getFullYear()}-${(clientDate.getMonth() + 1).toString().padStart(2, '0')}`;
                    acc[monthYear] = (acc[monthYear] || 0) + 1;
                    return acc;
                }, {});

                const chartData = Object.keys(monthlySignups).map(key => ({
                    name: key,
                    "Inscriptions": monthlySignups[key]
                })).sort((a, b) => a.name.localeCompare(b.name));

                // Calcul du taux de croissance
                const previousMonthClients = allClients.filter(client => {
                    if (!client.createdAt) return false;
                    const clientDate = new Date(client.createdAt);
                    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
                    const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

                    return clientDate.getMonth() === previousMonth &&
                        clientDate.getFullYear() === previousMonthYear;
                }).length;

                // Calculer le taux de croissance en pourcentage
                let growthRate = 0;
                if (previousMonthClients > 0) {
                    growthRate = ((newClientsThisMonth / previousMonthClients) * 100);
                } else if (newClientsThisMonth > 0) {
                    growthRate = 100; // Si pas de clients le mois précédent mais des nouveaux ce mois-ci
                }

                setSummaryData({
                    totalClients,
                    newClientsThisMonth,
                    chartData,
                    growthRate
                });
            } catch (err) {
                console.error("Erreur lors de la récupération des données de résumé :", err);
                setSummaryData({
                    totalClients: 0,
                    newClientsThisMonth: 0,
                    chartData: [],
                    growthRate: 0
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAllClients();
    }, [refreshKey]);

    return { summaryData, loading };
};


// Composant de modal pour la confirmation de suppression
const DeleteClientModal = ({ isOpen, onClose, client, onConfirmDelete }) => {
    if (!isOpen || !client) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-8 text-center">
                <FaTrash className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Êtes-vous sûr ?</h2>
                <p className="text-gray-600 mb-6">
                    Cette action est irréversible. Toutes les données de <b>{client.firstName} {client.lastName}</b> seront archivées et l'utilisateur sera supprimé de manière permanente.
                </p>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirmDelete(client.userId)}
                        className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                    >
                        Confirmer la suppression
                    </button>
                </div>
            </div>
        </div>
    );
};

// Composant de modal pour ajouter ou modifier un client
const ClientFormModal = ({ isOpen, onClose, clientToEdit, onSuccess }) => {
    const isEditing = !!clientToEdit;
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        address: '',
        city: '',
        password: '',
        userTypeId: 2,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Utilisez useEffect pour mettre à jour l'état du formulaire chaque fois que clientToEdit change
    useEffect(() => {
        if (isEditing && clientToEdit) {
            setFormData({
                ...clientToEdit,
                password: '', // Le mot de passe n'est jamais pré-rempli pour la sécurité
            });
        } else {
            // Réinitialise le formulaire lorsque le mode n'est pas la modification
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                address: '',
                city: '',
                password: '',
                userTypeId: 2,
            });
        }
    }, [clientToEdit, isEditing]); // Dépend de clientToEdit et isEditing

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            const method = isEditing ? 'PUT' : 'POST';
            const endpoint = isEditing ? `${API_URL}/edit-user/${clientToEdit.userId}` : `${API_URL}/post-user`;

            const res = await fetch(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(formData)
            });

            const data = await res.json();

            if (!res.ok) {
                const errorMessage = data.message || `Échec de ${isEditing ? 'la modification' : "l'ajout"} du client.`;
                throw new Error(errorMessage);
            }

            setSuccess(`Client ${isEditing ? 'modifié' : 'ajouté'} avec succès!`);
            onSuccess(formData);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err) {
            console.error("Erreur API:", err);
            setError(err.message || "Une erreur est survenue lors de l'opération.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{isEditing ? 'Modifier un client' : 'Ajouter un nouveau client'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" role="alert">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2" role="alert">
                        <FaCheckCircle className="text-green-500" />
                        <span>{success}</span>
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleFormChange}
                            placeholder="Prénom"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleFormChange}
                            placeholder="Nom de famille"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            placeholder="Email"
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleFormChange}
                            placeholder="Numéro de téléphone"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleFormChange}
                            placeholder="Addresse"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />

                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleFormChange}
                            placeholder="Ville"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleFormChange}
                            placeholder="Mot de passe"
                            required={!isEditing}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-colors disabled:bg-indigo-300"
                        >
                            {loading ? `Enregistrement...` : isEditing ? 'Sauvegarder les modifications' : 'Ajouter le client'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Composant de modal pour les détails du client
const ClientDetailsModal = ({ isOpen, onClose, client, onDeleteClient, onEditClient }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log("ClientDetailsModal: useEffect déclenché.");
        console.log("Client reçu dans la modal:", client);

        if (!isOpen || !client) {
            console.log("ClientDetailsModal: La modal est fermée ou le client est manquant. Arrêt du chargement.");
            setLoading(false);
            return;
        }

        const fetchOrders = async () => {
            console.log("ClientDetailsModal: Début de la récupération des commandes.");
            setLoading(true);
            setError(null);
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                console.error("ClientDetailsModal: Jeton d'authentification manquant.");
                setLoading(false);
                setError("Jeton d'authentification manquant. Veuillez vous reconnecter.");
                return;
            }

            try {
                // UPDATED API ENDPOINT
             
                const res = await fetch(`${API_URL}/clients/${client.userId}/orders`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (res.status === 401) {
                    throw new Error("Accès refusé. Jeton d'authentification invalide.");
                }

                if (!res.ok) {
                    throw new Error("Échec de la récupération des commandes.");
                }

                const responseData = await res.json();
                console.log("ClientDetailsModal: Données de réponse brutes de l'API:", responseData);

                // The backend now sends the filtered data directly, no need for manual filtering
                const clientOrders = responseData.orders;

                if (!Array.isArray(clientOrders)) {
                    console.error("ClientDetailsModal: Le format des données de commandes est incorrect. Ce n'est pas un tableau.");
                    throw new Error("Le format des données de commandes est incorrect.");
                }

                console.log("ClientDetailsModal: Commandes reçues:", clientOrders);
                setOrders(clientOrders);

            } catch (err) {
                console.error("ClientDetailsModal: Erreur API:", err);
                setError(err.message || "Une erreur est survenue. Veuillez vérifier la console pour plus de détails.");
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [isOpen, client]);

    if (!isOpen || !client) return null;

    const monthlySpending = orders.reduce((acc, order) => {
        const date = new Date(order.createdAt);
        const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        const total = parseFloat(order.totalAmount);

        if (acc[monthYear]) {
            acc[monthYear] += total;
        } else {
            acc[monthYear] = total;
        }
        return acc;
    }, {});

    const chartData = Object.keys(monthlySpending).map(key => ({
        name: key,
        "Dépenses ($)": monthlySpending[key]
    })).sort((a, b) => a.name.localeCompare(b.name));

    const totalSpent = orders.reduce((acc, order) => acc + parseFloat(order.totalAmount), 0);
    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'in_transit':
                return 'bg-blue-100 text-blue-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-filter backdrop-blur-sm flex justify-center items-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 my-8">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Profile de {client.firstName} {client.lastName}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-48">
                        <div className="text-xl text-gray-700 animate-pulse">Chargement des données...</div>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-sm" role="alert">
                        {error}
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Détails personnels du client */}
                        <div className="bg-gray-50 p-6 rounded-xl shadow-inner">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Informations personnelles</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                <p><strong>Nom:</strong> {client.firstName} {client.lastName}</p>
                                <p><strong>Email:</strong> {client.email}</p>
                                <p><strong>Téléphone:</strong> {client.phoneNumber || 'N/A'}</p>
                                <p><strong>Adresse:</strong> {client.address || 'N/A'}</p>
                                <p><strong>Ville:</strong> {client.city || 'N/A'}</p>
                                <p><strong>Type:</strong> <span className="bg-purple-100 text-purple-800 font-semibold py-1 px-3 rounded-full text-xs">{client.userType}</span></p>
                                <p><strong>Membre depuis:</strong> {new Date(client.createdAt).toLocaleDateString('fr-FR')}</p>
                            </div>
                        </div>

                        {/* Graphiques et statistiques */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white p-6 rounded-xl shadow-lg">
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Historique des dépenses mensuelles</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" tickFormatter={(tick) => tick.slice(5)} />
                                        <YAxis />
                                        <Tooltip formatter={(value) => `${value.toFixed(2)} $`} />
                                        <Line type="monotone" dataKey="Dépenses ($)" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg">
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Statistiques clés</h3>
                                <div className="grid grid-cols-2 gap-4 text-gray-700">
                                    <div className="p-4 bg-blue-50 rounded-lg">
                                        <p className="text-sm">Total des Commandes</p>
                                        <p className="text-2xl font-bold mt-1 text-blue-800">{orders.length}</p>
                                    </div>
                                    <div className="p-4 bg-green-50 rounded-lg">
                                        <p className="text-sm">Montant Total Dépensé</p>
                                        <p className="text-2xl font-bold mt-1 text-green-800">
                                            CDF {totalSpent.toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tableau de l'historique des commandes */}
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Historique des commandes</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                            <th className="py-3 px-6 text-left">ID de commande</th>
                                            <th className="py-3 px-6 text-left">Date de commande</th>
                                            <th className="py-3 px-6 text-left">Montant total</th>
                                            <th className="py-3 px-6 text-left">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                        {orders.length > 0 ? (
                                            orders.map(order => (
                                                <tr key={order.orderId} className="hover:bg-gray-50 transition-colors">
                                                    <td className="py-3 px-6 whitespace-nowrap">{order.orderId}</td>
                                                    <td className="py-3 px-6">{new Date(order.createdAt).toLocaleDateString('fr-FR')}</td>
                                                    <td className="py-3 px-6">CDF {parseFloat(order.totalAmount).toFixed(2)}</td>
                                                    <td className="py-3 px-6">
                                                        <span className={`inline-block px-3 py-1 mt-1 text-xs font-bold rounded-full ${getStatusStyle(order.status)}`}>
                                                            {statusMap[order.status]}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="py-10 text-center text-gray-500">Aucune commande trouvée pour ce client.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-8 pt-4 border-t flex justify-between">
                    <button
                        onClick={() => onEditClient(client)}
                        className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors"
                    >
                        <FaEdit />
                        <span>Modifier le client</span>
                    </button>
                    <button
                        onClick={() => onDeleteClient(client)}
                        className="flex items-center space-x-2 px-6 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors"
                    >
                        <FaTrash />
                        <span>Supprimer le client</span>
                    </button>

                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Composant principal de la page Clients
const ClientsPage = () => {
    const [inputTerm, setInputTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');

    const { clients, totalCount, loading, clientsPerPage } = useFetchClients(searchTerm, page, refreshKey);
    const { summaryData, loading: summaryLoading } = useClientSummaryData(refreshKey);

    const totalPages = Math.ceil(totalCount / clientsPerPage);

    const showMessage = (message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    };

    const handleInputChange = (e) => {
        setInputTerm(e.target.value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(inputTerm);
        setPage(1);
    };

    const handleSuccess = (clientData) => {
        setRefreshKey(prev => prev + 1);
        showMessage(`Client ${clientData.firstName} ${clientData.lastName} a été mis à jour avec succès!`);
    };

    const handleViewClient = (client) => {
        setSelectedClient(client);
        setIsViewModalOpen(true);
    };

    const handleEditClient = (client) => {
        setSelectedClient(client);
        setIsViewModalOpen(false);
        setIsEditModalOpen(true);
    };

    const handleDeleteClient = (client) => {
        setSelectedClient(client);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (userId) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const accessToken = localStorage.getItem("accessToken");

            const res = await fetch(`${API_URL}/delete-user/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Échec de la suppression de l'utilisateur.");
            }

            // Fermer les modals et rafraîchir la liste
            setIsDeleteModalOpen(false);
            setIsViewModalOpen(false);
            setRefreshKey(prev => prev + 1);

            showMessage("Client supprimé avec succès !");

        } catch (err) {
            console.error("Erreur lors de la suppression:", err);
            showMessage(err.message || "Une erreur est survenue lors de la suppression.", 'error');
        }
    };

    if (loading || summaryLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-2xl text-gray-700 animate-pulse">Chargement des données...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900">
                            Gestion des clients 👥
                        </h1>
                        <p className="mt-2 text-lg text-gray-500">
                            Visualisez et recherchez les clients de votre plateforme.
                        </p>
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
                    >
                        <FaPlus />
                        <span>Ajouter un client</span>
                    </button>
                </header>

                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        <FaCheckCircle className="text-green-500" />
                        <span>{statusMessage}</span>
                    </div>
                )}

                {/* NOUVELLE SECTION : Cartes de résumé et graphique */}
                {summaryData && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {/* Carte 1 : Total clients */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-indigo-100 text-indigo-600 rounded-full">
                                <FaUsers className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Total Clients</h3>
                                <p className="text-3xl font-bold text-gray-900">{summaryData.totalClients}</p>
                            </div>
                        </div>

                        {/* Carte 2 : Nouveaux clients ce mois-ci */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-green-100 text-green-600 rounded-full">
                                <FaUserPlus className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Nouveaux ce mois-ci</h3>
                                <p className="text-3xl font-bold text-gray-900">{summaryData.newClientsThisMonth}</p>
                            </div>
                        </div>

                        {/* Carte 3 : Taux de croissance */}
                        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                            <div className="p-4 bg-yellow-100 text-yellow-600 rounded-full">
                                <FaChartLine className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-500 uppercase">Croissance</h3>
                                <p className="text-3xl font-bold text-gray-900">
                                    {summaryData?.growthRate ? `+${summaryData.growthRate.toFixed(2)}%` : "0%"}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* NOUVELLE SECTION : Graphique des inscriptions */}
                {summaryData && (
                    <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Inscriptions mensuelles</h2>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={summaryData.chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickFormatter={(tick) => tick.slice(5)} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="Inscriptions" fill="#6366f1" radius={[10, 10, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Ancien contenu de la liste de clients */}
                <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
                    <form onSubmit={handleSearch}>
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="relative flex-1">
                                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={inputTerm}
                                    onChange={handleInputChange}
                                    placeholder="Rechercher par nom ou email..."
                                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                                />
                            </div>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                            >
                                Rechercher
                            </button>
                        </div>
                    </form>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-4 px-6 text-left rounded-tl-xl">Nom et prénom</th>
                                    <th className="py-4 px-6 text-left">Email</th>
                                    <th className="py-4 px-6 text-left">Téléphone</th>
                                    <th className="py-4 px-6 text-left">Adresse</th>
                                    <th className="py-4 px-6 text-left">Type de client</th>
                                    <th className="py-4 px-6 text-left">Date d'inscription</th>
                                    <th className="py-4 px-6 text-center rounded-tr-xl">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {clients.length > 0 ? (
                                    clients.map((client) => (
                                        <tr key={client.userId} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-4 px-6 text-left whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <FaUser className="w-5 h-5 text-gray-500" />
                                                    <span className="font-medium text-gray-800">
                                                        {client.firstName || ''} {client.lastName || ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-blue-600 font-medium">
                                                    {client.email || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500">
                                                    {client.phoneNumber || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500">
                                                    {client.address || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500">
                                                    {client.city || 'N/A'}
                                                </span>
                                            </td>

                                            <td className="py-4 px-6 text-left">
                                                <span className="bg-purple-100 text-purple-800 font-semibold py-1 px-3 rounded-full text-xs">
                                                    {client.userType}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500 text-sm">
                                                    {client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewClient(client)}
                                                        className="p-2 rounded-full text-indigo-500 hover:bg-indigo-100 transition-colors"
                                                        title="Voir les détails"
                                                    >
                                                        <FaEye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClient(client)}
                                                        className="p-2 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                        title="Supprimer le client"
                                                    >
                                                        <FaTrash className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="py-10 text-center text-gray-500">
                                            Aucun client trouvé.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-6">
                        <div className="text-sm text-gray-600">
                            Affichage de {(page - 1) * clientsPerPage + 1} à {Math.min(page * clientsPerPage, totalCount)} sur {totalCount} clients
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <FaAngleLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <FaAngleRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modal pour ajouter un nouveau client */}
            <ClientFormModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleSuccess}
                clientToEdit={null} // Pas de client pour l'ajout
            />
            {/* Modal pour modifier un client existant */}
            <ClientFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={handleSuccess}
                clientToEdit={selectedClient}
            />
            {/* Modal pour voir les détails du client */}
            <ClientDetailsModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                client={selectedClient}
                onDeleteClient={handleDeleteClient}
                onEditClient={handleEditClient}
            />
            {/* Modal de confirmation de suppression */}
            <DeleteClientModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                client={selectedClient}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};

export default ClientsPage;
